package com.evoapps.evoshift.observability

import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Promise

/** The modular Firebase Analytics JS SDK (npm `firebase`, the version GitLive brings). */
@JsModule("firebase/analytics")
@JsNonModule
external object FirebaseAnalyticsJs {
    fun initializeAnalytics(app: dynamic, options: dynamic): dynamic
    fun isSupported(): Promise<Boolean>
    fun logEvent(analytics: dynamic, name: String, params: dynamic)
    fun setAnalyticsCollectionEnabled(analytics: dynamic, enabled: Boolean)
    fun setConsent(consent: dynamic)
    fun setUserId(analytics: dynamic, id: String?)
}

@JsModule("firebase/app")
@JsNonModule
external object FirebaseAppJs {
    fun getApp(): dynamic
}

/**
 * Google Analytics for Firebase on the web (SH-86); Crashlytics has no web SDK, so crash reports
 * are not offered here. Nothing is loaded or initialised, and no cookie is set, until the person
 * allows usage statistics: only then is Consent Mode set to analytics granted (advertising always
 * denied) and Analytics initialised. Withdrawing turns collection off, denies consent again and
 * removes Google Analytics' `_ga` cookies.
 */
class WebTelemetryBackend : TelemetryBackend {
    private var analytics: dynamic = null
    private var enabled = false
    private var userId: String? = null

    override fun setCollection(analytics: Boolean, crashes: Boolean, initial: Boolean) {
        enabled = analytics
        if (analytics) start() else stop(initial)
    }

    private fun start() {
        val current = analytics
        if (current != null) {
            FirebaseAnalyticsJs.setConsent(consent(granted = true))
            FirebaseAnalyticsJs.setAnalyticsCollectionEnabled(current, true)
            return
        }
        FirebaseAnalyticsJs.isSupported().then { supported ->
            if (!supported || !enabled || analytics != null) return@then
            FirebaseAnalyticsJs.setConsent(consent(granted = true))
            val options: dynamic = js("({})")
            options.config = js("({ allow_google_signals: false, allow_ad_personalization_signals: false })")
            val sdk: dynamic = runCatching { FirebaseAnalyticsJs.initializeAnalytics(FirebaseAppJs.getApp(), options) }.getOrNull()
            analytics = sdk
            val uid = userId
            if (sdk != null && uid != null) FirebaseAnalyticsJs.setUserId(sdk, uid)
        }
    }

    private fun stop(initial: Boolean) {
        val current = analytics
        if (current != null) {
            FirebaseAnalyticsJs.setAnalyticsCollectionEnabled(current, false)
            FirebaseAnalyticsJs.setConsent(consent(granted = false))
        }
        // Never initialised in this page and not withdrawn now: nothing to clean up.
        if (current != null || !initial) removeAnalyticsCookies()
    }

    override fun logEvent(name: String, params: Map<String, String>) {
        val sdk = analytics ?: return
        if (!enabled) return
        val bag: dynamic = js("({})")
        params.forEach { (key, value) -> bag[key] = value }
        FirebaseAnalyticsJs.logEvent(sdk, name, bag)
    }

    override fun recordNonFatal(throwable: Throwable) = Unit

    override fun setUserId(uid: String?) {
        userId = uid
        val sdk: dynamic = analytics
        if (sdk != null) FirebaseAnalyticsJs.setUserId(sdk, uid)
    }

    override fun setCrashKey(key: String, value: String) = Unit

    private fun consent(granted: Boolean): dynamic {
        val value: dynamic = js("({})")
        value.analytics_storage = if (granted) "granted" else "denied"
        value.ad_storage = "denied"
        value.ad_user_data = "denied"
        value.ad_personalization = "denied"
        return value
    }

    /** Deletes `_ga` / `_ga_<id>` on this host and its parent domains. */
    private fun removeAnalyticsCookies() {
        val names = runCatching { document.cookie }.getOrDefault("").split(';')
            .map { it.substringBefore('=').trim() }
            .filter { it == "_ga" || it.startsWith("_ga_") }
        if (names.isEmpty()) return
        val parts = window.location.hostname.split('.')
        val domains = listOf("") + parts.indices.map { "; domain=." + parts.drop(it).joinToString(".") }
        names.forEach { name ->
            domains.forEach { domain ->
                runCatching { document.cookie = "$name=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/$domain" }
            }
        }
    }
}

/**
 * Only the hosted app collects: never on localhost (development, `?emulator`, the web smoke test).
 */
fun webTelemetryPlatform(): TelemetryPlatform {
    val host = window.location.hostname
    val local = host == "localhost" || host == "127.0.0.1" || host == "[::1]" || host.endsWith(".localhost") || host.isEmpty()
    val emulator = window.location.search.contains("emulator")
    return TelemetryPlatform(canCollect = !local && !emulator, supportsCrashReports = false)
}
